import type { FoundryV2QaFactoryRecord } from "../api/foundryV2Client";

function statusTone(status: "ready" | "watch" | "blocked"): string {
  switch (status) {
    case "ready":
      return "border-emerald-200 bg-emerald-50 text-emerald-700";
    case "watch":
      return "border-amber-200 bg-amber-50 text-amber-700";
    default:
      return "border-red-200 bg-red-50 text-red-700";
  }
}

type Props = {
  qaFactory: FoundryV2QaFactoryRecord | null;
  isLoading: boolean;
  error: string | null;
};

export function FoundryQaFactoryPanel({ qaFactory, isLoading, error }: Props): JSX.Element {
  const report = qaFactory?.report ?? null;
  const artifactUrls = qaFactory?.artifactUrls ?? null;

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f8fbff_100%)] p-5 shadow-[0_20px_60px_rgba(15,23,42,0.08)]"
      data-testid="foundry-qa-factory-panel"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl">
          <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">QA Factory v1</div>
          <h3 className="mt-2 text-xl font-semibold text-slate-950">
            Governed acceptance criteria, edge cases, test plan, and Playwright templates before Jarvis runs
          </h3>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            This panel turns design truth into executable QA truth. If criteria, edge cases, or Playwright coverage are missing,
            Jarvis should not be treated as launch-ready.
          </p>
        </div>
      </div>

      {error ? <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div> : null}
      {isLoading && !qaFactory ? (
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          Loading QA factory...
        </div>
      ) : null}

      {report ? (
        <>
          <div className="mt-5 grid gap-3 xl:grid-cols-[1.05fr_0.95fr_0.95fr_0.9fr]">
            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="flex items-center gap-2">
                <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${statusTone(report.summary.status)}`}>
                  {report.summary.status}
                </span>
                <span className="text-xs text-slate-500">coverage {report.summary.coverageScore}</span>
              </div>
              <div className="mt-3 text-sm text-slate-700">{report.summary.headline}</div>
              <div className="mt-3 grid gap-2 text-xs text-slate-600 md:grid-cols-2">
                <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  {report.summary.acceptanceCriteriaCount} acceptance criteria
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  {report.summary.edgeCaseCount} edge case(s)
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  {report.summary.playwrightTemplateCount} Playwright template(s)
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  Promotion {report.summary.canPromote ? "allowed" : "blocked"}
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Founder view</div>
              <ul className="mt-3 space-y-2 text-xs text-slate-600">
                {report.readableViews.founder.map((item) => (
                  <li key={item} className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">{item}</li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Tactical view</div>
              <ul className="mt-3 space-y-2 text-xs text-slate-600">
                {report.readableViews.tactical.map((item) => (
                  <li key={item} className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">{item}</li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Artifacts</div>
              <div className="mt-3 space-y-2 text-xs">
                <a className="block font-medium text-blue-700 underline" href={artifactUrls?.qaFactoryReport ?? "#"}>
                  qa_factory_report.v1.json
                </a>
                <a className="block font-medium text-blue-700 underline" href={artifactUrls?.playwrightTemplate ?? "#"}>
                  qa_playwright_template.v1.json
                </a>
              </div>
            </div>
          </div>

          <div className="mt-5 grid gap-4 xl:grid-cols-[1.05fr_0.95fr]">
            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Acceptance criteria</div>
              <div className="mt-3 space-y-3">
                {report.acceptanceCriteria.map((criterion) => (
                  <div key={criterion.id} className="rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-semibold text-slate-900">{criterion.title}</div>
                      <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${criterion.mustPass ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-600"}`}>
                        {criterion.mustPass ? "must pass" : "watch"}
                      </span>
                    </div>
                    <div className="mt-2 text-xs text-slate-600">{criterion.description}</div>
                    <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-slate-500">
                      <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5">{criterion.source}</span>
                      {criterion.evidenceRefs.map((evidenceRef) => (
                        <span key={`${criterion.id}-${evidenceRef}`} className="rounded-full border border-slate-200 bg-white px-2 py-0.5">
                          {evidenceRef}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Edge cases</div>
                <div className="mt-3 space-y-3 text-xs text-slate-600">
                  {report.edgeCases.map((edgeCase) => (
                    <div key={edgeCase.id} className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="font-semibold text-slate-800">{edgeCase.title}</div>
                        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                          edgeCase.risk === "critical"
                            ? "border-red-200 bg-red-50 text-red-700"
                            : edgeCase.risk === "high"
                              ? "border-amber-200 bg-amber-50 text-amber-700"
                              : "border-slate-200 bg-white text-slate-600"
                        }`}>
                          {edgeCase.risk}
                        </span>
                      </div>
                      <div className="mt-2">{edgeCase.scenario}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Playwright templates</div>
                <div className="mt-3 space-y-3 text-xs text-slate-600">
                  {report.playwrightTemplates.map((template) => (
                    <div key={template.id} className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-3">
                      <div className="text-sm font-semibold text-slate-900">{template.title}</div>
                      <div className="mt-2">{template.objective}</div>
                      <div className="mt-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Steps</div>
                      <ul className="mt-2 space-y-1">
                        {template.steps.map((step) => <li key={step}>- {step}</li>)}
                      </ul>
                      <div className="mt-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Assertions</div>
                      <ul className="mt-2 space-y-1">
                        {template.assertions.map((assertion) => <li key={assertion}>- {assertion}</li>)}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Test plan</div>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  {([
                    ["Prepare", report.testPlan.prepare],
                    ["API", report.testPlan.api],
                    ["UI", report.testPlan.ui],
                    ["Release", report.testPlan.release],
                  ] as const).map(([label, items]) => (
                    <div key={label} className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-3 text-xs text-slate-600">
                      <div className="font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</div>
                      <ul className="mt-2 space-y-1">
                        {items.map((item) => <li key={item}>- {item}</li>)}
                      </ul>
                    </div>
                  ))}
                </div>
                {report.blockers.length ? (
                  <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-3 text-xs text-red-800">
                    {report.blockers.map((blocker) => <div key={blocker}>- {blocker}</div>)}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </>
      ) : null}
    </section>
  );
}
